/**
 * The wire between the Chrome extension and the Floto web app.
 *
 * The extension exists for the one thing the browser worker cannot do: capture
 * a page that only the user's own machine can reach (`localhost:3000`, a
 * staging host behind a VPN). It holds no credentials of its own — it hands the
 * PNG to an already signed-in Floto tab, which uploads it through the same
 * three-call path `/browse` uses. So the messages below are the whole contract:
 * a capture going one way, its outcome coming back.
 *
 * Two hops in each direction, because a page and an extension cannot talk
 * directly:
 *
 *   background --chrome.tabs.sendMessage--> bridge content script
 *              --window.postMessage-------> the web app
 *              --window.postMessage-------> bridge content script
 *              --chrome.runtime.sendMessage-> background
 *
 * Both ends therefore filter on `source` and `version`: a page's `message`
 * event sees every postMessage on the window, including its own and any other
 * extension's.
 *
 * Ships as TypeScript source (see this package's `exports`), like
 * `browser-protocol`, because it has runtime values and both consumers are
 * bundlers rather than Node.
 */
export const EXTENSION_BRIDGE_SOURCE = 'floto-canvas-extension';

/**
 * Bumped when a message shape changes incompatibly. An extension and a web app
 * are updated independently — a user can be running last month's extension
 * against today's build — so the receiver ignores what it does not understand
 * rather than crashing on it.
 */
export const EXTENSION_BRIDGE_VERSION = 1;

/**
 * The same three values as `CaptureDevice`, spelled out rather than imported.
 *
 * This file is consumed as *source* by two bundlers, and the entity that owns
 * that type is a `class-validator` DTO class — importing it drags the whole
 * class graph into a type-check that, unlike Nest's, has
 * `strictPropertyInitialization` on, and every DTO field then errors. The web
 * app assigns this straight into `CreateCanvasCaptureDto['device']`, so a drift
 * between the two would fail to compile there.
 */
export type ExtensionCaptureDevice = 'desktop' | 'tablet' | 'mobile';

/** Why a capture did not land, in terms the extension can render on a badge. */
export type ExtensionCaptureFailure =
  'signed-out' | 'unusable-url' | 'upload-failed';

/** One screenshot, as the extension took it. */
export interface ExtensionCapturePayload {
  /** The tab's URL — becomes the capture's `pageUrl` and picks the app. */
  pageUrl: string;
  /** Base64 PNG **without** the `data:image/png;base64,` prefix. */
  image: string;
  /** CSS pixels, not device pixels: the PNG is `width * dpr` wide. */
  width: number;
  height: number;
  dpr: number;
  device: ExtensionCaptureDevice;
}

/**
 * The same four values as `Issue['severity']`, spelled out rather than
 * imported — same reason as `ExtensionCaptureDevice` above: `Issue` lives in
 * a package built with `strictPropertyInitialization` off for Nest's sake,
 * and importing it into either bundler's own (stricter) type-check would
 * error on every one of its other fields.
 */
export type ExtensionIssueSeverity =
  'Critical' | 'Major' | 'Minor' | 'Suggestion';

/**
 * What the picker can read straight off the DOM element the PM clicked, for
 * the coding agent to search the codebase with instead of only a typed
 * title/description. `textContent` turns "the button that says X" into an
 * exact `search_code("X")` hit; `className` is the strongest signal of all in
 * a Tailwind codebase, since a utility class usually appears verbatim in
 * source. Every field is best-effort and optional — an SVG's `className` is
 * an `SVGAnimatedString`, not a plain string, and plenty of elements have no
 * image or no inline color at all.
 *
 * `textContent` is truncated (`run-picker.ts`) rather than sending the whole
 * `outerHTML`: a real customer's live page can render something sensitive
 * near the picked element (a signed-in email, an order number) that has no
 * business riding along in an issue report.
 */
export interface ExtensionElementContext {
  tagName: string;
  className?: string;
  id?: string;
  /** Trimmed and capped at ~200 characters. */
  textContent?: string;
  /** The picked element's own `<img src>`, or a `background-image` URL read
   *  off its computed style — whichever the picker found. */
  imageSrc?: string;
  /** `getComputedStyle(...).color` / `.backgroundColor`, as the browser
   *  resolved them — a fallback for when the color comes from a CSS custom
   *  property or inline style rather than a literal utility class. */
  computedColor?: string;
  computedBackgroundColor?: string;
}

/**
 * A PM's pick, from the picker overlay.
 *
 * A report is always about one picked element. A region is *not* a second
 * kind of subject — it is an optional screenshot the reporter may draw
 * inside the form to show what an element alone cannot ("these two don't
 * line up", "too much space here"), and it arrives as `regionShot` rather
 * than as its own context shape. An earlier revision sent a region's
 * contained elements as a list of up to twenty selectors *instead of* an
 * element context, which was a worse answer to the same question: the
 * natural output of drawing a shape is a picture of it, and the selector
 * list was standing in for the screenshot we hadn't built yet.
 */
export interface ExtensionReportIssuePayload {
  /** The tab's URL — becomes the issue's app, same as a capture's. */
  pageUrl: string;
  /** CSS-px bounding box of the picked element, "x,y WxH" — same convention
   *  as `Issue.rect`. */
  rect: string;
  /**
   * The two things the picker asks for, and the same pair every
   * rule-driven `skillType` already produces — so a manual report renders
   * on the fix list exactly like a design-audit finding does, rather than
   * being a differently-shaped row that happens to sit in the same list.
   *
   * There is deliberately no third "what is it" field feeding
   * `Issue.locationName`. Typed, it invited a second sentence that just
   * restated the description; derived from the element, it produced things
   * like "Every site you've brought onto the canvas." — a whole paragraph
   * of body text masquerading as a name. Neither is legible on a card, so
   * a manual report carries no location and the fix list omits that line.
   * `elementContext` is what actually locates it for the coding agent.
   */
  description: string;
  /** Optional: plenty of reports are "this is wrong" with no view on the
   *  fix. Empty is rendered as no Recommendation block at all. */
  recommendation: string;
  severity: ExtensionIssueSeverity;
  /** Absent only if the picker couldn't read anything useful off the
   *  element — the report still goes through on the description alone. */
  elementContext?: ExtensionElementContext;
  /** Present only when the reporter deliberately drew a region in the form.
   *  Nothing infers this and nothing attaches it on the reporter's behalf:
   *  the draw *is* the opt-in, which is also why `PrAgentService` hands it
   *  to the model whenever it exists rather than second-guessing whether
   *  the element context was already good enough. */
  regionShot?: ExtensionRegionShot;
}

/** A cropped screenshot of an area the reporter drew, plus where it came
 *  from. Base64 PNG **without** the `data:image/png;base64,` prefix, the
 *  same convention `ExtensionCapturePayload.image` follows. */
export interface ExtensionRegionShot {
  image: string;
  /** CSS-px bounding box of the drawn region, "x,y WxH". Kept alongside the
   *  crop because the crop alone says nothing about where on the page it
   *  was taken from. */
  rect: string;
}

interface BridgeEnvelope {
  source: typeof EXTENSION_BRIDGE_SOURCE;
  version: number;
}

/**
 * The web app announcing it is listening.
 *
 * Sent on mount and answered by nothing: a capture may arrive before the tab
 * that will handle it exists, so the extension opens one and waits for this
 * rather than guessing how long a cold load takes.
 */
export interface ExtensionReadyMessage extends BridgeEnvelope {
  kind: 'ready';
}

/**
 * The extension answering "yes, I am here", sent in reply to `ready`.
 *
 * A reply rather than an unprompted announcement, because the content script is
 * injected at `document_start` and the web app's listener mounts much later —
 * anything the bridge said on injection would be said to nobody. It is also why
 * the app needs no extension id and no `externally_connectable` entry to detect
 * the extension: the bridge is already the private channel between them.
 */
export interface ExtensionInstalledMessage extends BridgeEnvelope {
  kind: 'installed';
  /** The extension's own version, for "update the extension" messaging later. */
  extensionVersion: string;
}

export interface ExtensionCaptureMessage extends BridgeEnvelope {
  kind: 'capture';
  /** Correlates the result below; one capture may be in flight per click. */
  requestId: string;
  capture: ExtensionCapturePayload;
}

export interface ExtensionCaptureResultMessage extends BridgeEnvelope {
  kind: 'capture-result';
  requestId: string;
  ok: boolean;
  /** Present when `ok` is false. */
  failure?: ExtensionCaptureFailure;
  /**
   * What actually went wrong, in the API's own words.
   *
   * The three `failure` kinds are what the extension can *act* on; this is what
   * makes a failure diagnosable at all. Without it every 4xx read as "Floto
   * cannot make a board for this URL" — including an expired session and a
   * bucket refusing the upload, neither of which has anything to do with the
   * URL.
   */
  message?: string;
  /** Present when `ok` is true — enough to deep-link to the board. */
  projectId?: string;
  sessionId?: string;
  captureId?: string;
}

export interface ExtensionReportIssueMessage extends BridgeEnvelope {
  kind: 'report-issue';
  /** Correlates the result below; one report may be in flight at a time. */
  requestId: string;
  issue: ExtensionReportIssuePayload;
}

export interface ExtensionReportIssueResultMessage extends BridgeEnvelope {
  kind: 'report-issue-result';
  requestId: string;
  ok: boolean;
  /** Present when `ok` is false — the same kinds a capture can fail with;
   *  nothing about picking or describing an issue introduces a new one. */
  failure?: ExtensionCaptureFailure;
  message?: string;
  /** Present when `ok` is true. */
  projectId?: string;
  issueId?: string;
}

export type ExtensionBridgeMessage =
  | ExtensionCaptureMessage
  | ExtensionCaptureResultMessage
  | ExtensionInstalledMessage
  | ExtensionReadyMessage
  | ExtensionReportIssueMessage
  | ExtensionReportIssueResultMessage;

const KINDS = new Set([
  'capture',
  'capture-result',
  'installed',
  'ready',
  'report-issue',
  'report-issue-result',
]);

/** Narrows an untrusted `window.message` / `chrome.runtime` payload. */
export function isExtensionBridgeMessage(
  value: unknown,
): value is ExtensionBridgeMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<BridgeEnvelope> & { kind?: unknown };
  return (
    message.source === EXTENSION_BRIDGE_SOURCE &&
    message.version === EXTENSION_BRIDGE_VERSION &&
    typeof message.kind === 'string' &&
    KINDS.has(message.kind)
  );
}
